import type { SimView } from "../core/simView";
import { modelRegistry } from "../plugins";
import { initPositions, initTopology } from "./init";
import { applyMotionLayer, motionIsIdentity, motionNeedsHash } from "./motion";
import { cloneSimParams, type SimParams, validateSimParams } from "./params";
import {
  copyPlanes,
  getRelationProfile,
  resolveRoleWeights,
} from "./relationProfile";
import { makeRng, type Rng } from "./rng";
import { SpatialHash2D } from "./spatialHash";
import {
  defaultSpawnPolicy,
  resolveSpawnLinks,
  type SpawnPolicy,
  type SpawnResultMeta,
} from "./spawnPolicy";
import { woodsStep } from "./step";

export class Simulation implements SimView {
  params: SimParams;
  positions: Float64Array;
  prevPositions: Float64Array;
  /** Track M velocity state (inertial); zero when kinematic */
  velocities: Float64Array;
  /** Planar packed targets length n*roleCount — stable until setTopologyBuffers */
  targets: Int32Array;
  /** Stable plane-0 view (rebound only in setTopologyBuffers) */
  friends: Int32Array;
  /** Stable plane-1 view */
  enemies: Int32Array;
  roleCount = 2;
  types: Uint8Array | null = null;
  tick = 0;
  /**
   * Bumps when friend/enemy pointers change (rechoose, rewire, reset).
   * Cycle-color cache invalidates on this — DESIGNS-TRACK-X §2.
   */
  topologyEpoch = 0;

  private rng: Rng;
  private scratchPull: Float64Array;
  private scratchPush: Float64Array;
  private scratchGravity: Float64Array;
  private nextPositions: Float64Array;
  private readonly hash = new SpatialHash2D(0.35);
  private lastTargetsSnapshot: Int32Array | null = null;
  private lastTypesSnapshot: Uint8Array | null = null;
  private roleWeightsBuf = new Float64Array(2);

  constructor(params: SimParams) {
    this.params = validateSimParams(cloneSimParams(params));
    this.rng = makeRng(this.params.seed);
    this.positions = initPositions(this.params, this.rng);
    const topo = initTopology(this.params, this.rng);
    this.targets = topo.targets;
    this.friends = topo.friends;
    this.enemies = topo.enemies;
    this.roleCount = topo.roleCount;
    this.types = topo.types;
    this.prevPositions = this.positions.slice();
    const len = this.params.n * this.params.dim;
    this.velocities = new Float64Array(len);
    this.scratchPull = new Float64Array(len);
    this.scratchPush = new Float64Array(len);
    this.scratchGravity = new Float64Array(len);
    this.nextPositions = new Float64Array(len);
    this.topologyEpoch = 1;
  }

  /**
   * Only place that rebinds friends/enemies views — after alloc/replace of targets.
   * DESIGNS-TRACK-R §2.3
   */
  private setTopologyBuffers(
    targets: Int32Array,
    types: Uint8Array | null,
    n: number,
    R: number,
  ): void {
    if (targets.length !== n * R) {
      throw new Error(`targets length ${targets.length} !== n*R (${n}*${R})`);
    }
    if (types && types.length !== n) {
      throw new Error(`types length ${types.length} !== n (${n})`);
    }
    this.targets = targets;
    this.roleCount = R;
    this.friends = targets.subarray(0, n);
    this.enemies = R >= 2 ? targets.subarray(n, 2 * n) : targets.subarray(0, 0);
    this.types = types;
  }

  get n(): number {
    return this.params.n;
  }

  get dim(): number {
    return this.params.dim;
  }

  writeSpeeds(out: Float64Array): void {
    const { dim, n } = this.params;
    const useStateV =
      this.params.motionMode === "inertial" && this.params.model !== "reynolds";
    for (let i = 0; i < n; i++) {
      let s = 0;
      const base = i * dim;
      for (let d = 0; d < dim; d++) {
        const v = useStateV
          ? this.velocities[base + d]!
          : this.positions[base + d]! - this.prevPositions[base + d]!;
        s += v * v;
      }
      out[i] = Math.sqrt(s);
    }
  }

  /** Spatial hash rebuilt when spatialHash or localTopology is on. */
  get spatialHash(): SpatialHash2D {
    return this.hash;
  }

  step(steps = 1): void {
    const model =
      modelRegistry.tryGet(this.params.model) ??
      modelRegistry.get("friends_enemies");

    for (let s = 0; s < steps; s++) {
      this.prevPositions.set(this.positions);

      const needHash =
        this.params.spatialHash ||
        this.params.localTopology ||
        this.params.model === "reynolds" ||
        motionNeedsHash(this.params);
      if (needHash) {
        const cell =
          this.params.gravityMode === "mutual" && this.params.gravityRadius > 0
            ? this.params.gravityRadius
            : this.params.neighborRadius;
        this.hash.setCellSize(cell);
        this.hash.rebuild(this.positions, this.params.dim, this.params.n);
      }

      // Snapshot topology to detect rechoose mutations
      const tLen = this.targets.length;
      if (
        !this.lastTargetsSnapshot ||
        this.lastTargetsSnapshot.length !== tLen
      ) {
        this.lastTargetsSnapshot = new Int32Array(tLen);
      }
      this.lastTargetsSnapshot.set(this.targets);
      if (this.types) {
        if (
          !this.lastTypesSnapshot ||
          this.lastTypesSnapshot.length !== this.types.length
        ) {
          this.lastTypesSnapshot = new Uint8Array(this.types.length);
        }
        this.lastTypesSnapshot.set(this.types);
      }

      const weights = resolveRoleWeights(this.params);
      if (this.roleWeightsBuf.length !== weights.length) {
        this.roleWeightsBuf = new Float64Array(weights.length);
      }
      this.roleWeightsBuf.set(weights);

      model.step({
        params: this.params,
        positions: this.positions,
        outPositions: this.nextPositions,
        friends: this.friends,
        enemies: this.enemies,
        targets: this.targets,
        roleCount: this.roleCount,
        roleWeights: this.roleWeightsBuf,
        types: this.types,
        scratchA: this.scratchPull,
        scratchB: this.scratchPush,
        rng: this.rng,
        spatialHash: needHash ? this.hash : null,
      });

      if (!motionIsIdentity(this.params)) {
        applyMotionLayer(
          this.positions,
          this.nextPositions,
          this.velocities,
          this.scratchGravity,
          this.params,
          this.params.n,
          this.params.dim,
          {
            types: this.types,
            spatialHash: needHash ? this.hash : null,
          },
        );
      }

      let changed = topologyChanged(this.lastTargetsSnapshot, this.targets);
      if (!changed && this.types && this.lastTypesSnapshot) {
        changed = typesChanged(this.lastTypesSnapshot, this.types);
      }
      if (changed) this.topologyEpoch++;

      const tmp = this.positions;
      this.positions = this.nextPositions;
      this.nextPositions = tmp;

      // Keep velocities as projective display state when kinematic (finite-diff).
      // Inertial path already wrote true v inside applyMotionLayer.
      const inertial =
        this.params.motionMode === "inertial" &&
        this.params.model !== "reynolds";
      if (!inertial) {
        const len = this.positions.length;
        for (let i = 0; i < len; i++) {
          this.velocities[i] = this.positions[i]! - this.prevPositions[i]!;
        }
      }

      this.tick += 1;
    }
  }

  setParams(params: SimParams, opts: { reset?: boolean } = {}): void {
    const next = validateSimParams(cloneSimParams(params));
    const resize =
      next.n !== this.params.n ||
      next.dim !== this.params.dim ||
      next.gridExtent !== this.params.gridExtent ||
      next.gridJitter !== this.params.gridJitter ||
      opts.reset === true;
    this.params = next;
    if (resize) this.reset();
  }

  reset(): void {
    this.rng = makeRng(this.params.seed);
    this.positions = initPositions(this.params, this.rng);
    const topo = initTopology(this.params, this.rng);
    this.setTopologyBuffers(
      topo.targets,
      topo.types,
      this.params.n,
      topo.roleCount,
    );
    this.prevPositions = this.positions.slice();
    const len = this.params.n * this.params.dim;
    this.velocities = new Float64Array(len);
    this.scratchPull = new Float64Array(len);
    this.scratchPush = new Float64Array(len);
    this.scratchGravity = new Float64Array(len);
    this.nextPositions = new Float64Array(len);
    this.tick = 0;
    this.topologyEpoch++;
  }

  reseed(seed?: number): void {
    this.params.seed = seed ?? (Math.random() * 0xffffffff) >>> 0;
    this.reset();
  }

  /** Set friend of i to j (−1 → random valid target). */
  rewireFriend(i: number, j: number): void {
    const n = this.n;
    if (i < 0 || i >= n) return;
    this.friends[i] = j >= 0 && j < n ? j : this.randomTarget(i);
    if (this.params.forbidSelfLinks && this.friends[i] === i) {
      this.friends[i] = this.randomTarget(i);
    }
    this.topologyEpoch++;
  }

  rewireEnemy(i: number, j: number): void {
    const n = this.n;
    if (i < 0 || i >= n) return;
    this.enemies[i] = j >= 0 && j < n ? j : this.randomTarget(i);
    if (this.params.forbidSelfLinks && this.enemies[i] === i) {
      this.enemies[i] = this.randomTarget(i);
    }
    this.topologyEpoch++;
  }

  /** Rewire one random particle’s friend pointer (choreography disturb). */
  disturbRandomFriend(): void {
    const i = this.rng.int(this.n);
    this.rewireFriend(i, -1);
  }

  /**
   * Soft cap for click-to-spawn (keeps buffers / WebGL sane for exhibit use).
   * Slider / presets may still set higher n via full reset.
   */
  static readonly SPAWN_MAX_N = 20_000;

  /**
   * Append one particle at world position without resetting the swarm.
   * Policy chooses newcomer friend/enemy and optional inbound rewires.
   * @returns { index, meta } or null if at cap / empty
   */
  addParticleAt(
    world: ArrayLike<number>,
    policy?: SpawnPolicy,
  ): { index: number; meta: SpawnResultMeta } | null {
    const n0 = this.params.n;
    if (n0 < 1 || n0 >= Simulation.SPAWN_MAX_N) return null;
    const dim = this.params.dim;
    const n1 = n0 + 1;
    const len1 = n1 * dim;
    const R = this.roleCount;
    const pol =
      policy ??
      defaultSpawnPolicy(
        Math.max(
          0.15,
          this.params.neighborRadius > 0 ? this.params.neighborRadius : 0.35,
        ),
      );

    const meta = resolveSpawnLinks(
      {
        positions: this.positions,
        dim,
        n0,
        w: world,
        localRadius: pol.localRadius,
        forbidSelf: this.params.forbidSelfLinks,
        int: (m) => this.rng.int(m),
      },
      pol,
    );

    const positions = new Float64Array(len1);
    const prev = new Float64Array(len1);
    const next = new Float64Array(len1);
    const pull = new Float64Array(len1);
    const push = new Float64Array(len1);
    const grav = new Float64Array(len1);
    const vel = new Float64Array(len1);
    const newTargets = new Int32Array(n1 * R);

    positions.set(this.positions);
    prev.set(this.prevPositions);
    vel.set(this.velocities);
    copyPlanes(this.targets, newTargets, n0, n1, R);

    const base = n0 * dim;
    for (let d = 0; d < dim; d++) {
      const v = world[d] ?? 0;
      positions[base + d] = v;
      prev[base + d] = v;
      // newcomer velocity = 0 (vel already zero-filled beyond n0)
    }

    // Newcomer outgoing roles
    newTargets[0 * n1 + n0] = meta.friend;
    if (R >= 2) newTargets[1 * n1 + n0] = meta.enemy;

    // Inbound: existing agents point at newcomer
    for (const ed of meta.inbound) {
      if (ed.agent < 0 || ed.agent >= n0) continue;
      const plane = ed.role === "friend" ? 0 : 1;
      if (plane < R) newTargets[plane * n1 + ed.agent] = n0;
    }

    let newTypes: Uint8Array | null = null;
    if (this.types || this.params.typeCount > 1) {
      newTypes = new Uint8Array(n1);
      if (this.types) newTypes.set(this.types.subarray(0, n0));
      // inherit_nearest type
      newTypes[n0] = this.spawnTypeInheritNearest(world, n0);
    }

    this.positions = positions;
    this.prevPositions = prev;
    this.velocities = vel;
    this.nextPositions = next;
    this.scratchPull = pull;
    this.scratchPush = push;
    this.scratchGravity = grav;
    this.params = { ...this.params, n: n1 };
    this.setTopologyBuffers(newTargets, newTypes, n1, R);
    this.topologyEpoch++;
    return { index: n0, meta };
  }

  /** Type of geometrically nearest existing agent (inherit_nearest). */
  private spawnTypeInheritNearest(
    world: ArrayLike<number>,
    n0: number,
  ): number {
    if (!this.types || n0 <= 0) return 0;
    const dim = this.params.dim;
    let bestJ = 0;
    let bestD = Infinity;
    for (let j = 0; j < n0; j++) {
      let s = 0;
      const base = j * dim;
      for (let d = 0; d < dim; d++) {
        const dd = this.positions[base + d]! - (world[d] ?? 0);
        s += dd * dd;
      }
      if (s < bestD || (s === bestD && j < bestJ)) {
        bestD = s;
        bestJ = j;
      }
    }
    return this.types[bestJ] ?? 0;
  }

  private randomTarget(selfIdx: number): number {
    return this.randomTargetAmong(this.n, selfIdx);
  }

  /** Random index in [0, n) optionally ≠ selfIdx when forbidSelfLinks. */
  private randomTargetAmong(n: number, selfIdx: number): number {
    if (n <= 0) return 0;
    if (!this.params.forbidSelfLinks || n === 1) return this.rng.int(n);
    if (n === 1) return 0;
    let j = this.rng.int(n - 1);
    if (j >= selfIdx) j += 1;
    return j;
  }
}

function topologyChanged(before: Int32Array, after: Int32Array): boolean {
  for (let i = 0; i < after.length; i++) {
    if (before[i] !== after[i]) return true;
  }
  return false;
}

function typesChanged(before: Uint8Array, after: Uint8Array): boolean {
  for (let i = 0; i < after.length; i++) {
    if (before[i] !== after[i]) return true;
  }
  return false;
}

/** Pure step helper for tests (allocating). */
export function woodsStepAlloc(
  positions: Float64Array,
  friends: Int32Array,
  enemies: Int32Array,
  params: SimParams,
): Float64Array {
  const out = new Float64Array(positions.length);
  const pull = new Float64Array(positions.length);
  const push = new Float64Array(positions.length);
  woodsStep(positions, friends, enemies, params, out, pull, push);
  return out;
}

// Ensure profile is loadable (side-effect free import for tree)
void getRelationProfile;
